import type { QueryClient } from '@tanstack/react-query';
import { type UserResponse } from '@packages/shared/domain/user/schemas/UserResponseSchemas';
import { UserRole } from '@packages/shared/domain/user/UserRole';
import { isRedirect, redirect } from '@tanstack/react-router';

import { Routes } from '@/config/Routes';
import { getCurrentUser, logoutUser } from '@/features/auth/api/auth.api';
import { AUTH_STALE_TIME, authKeys } from '@/features/auth/hooks/authKeys';

const MOBILE_ALLOWED_ROLES = new Set<string>([UserRole.TECHNICIAN, UserRole.DUAL]);

export class AuthGuard {
	static async requireAuthenticated(queryClient: QueryClient): Promise<UserResponse> {
		try {
			const user = await queryClient.ensureQueryData({
				queryKey: authKeys.currentUser(),
				queryFn: getCurrentUser,
				staleTime: AUTH_STALE_TIME,
			});

			if (!MOBILE_ALLOWED_ROLES.has(user.role)) {
				await logoutUser();
				queryClient.removeQueries({ queryKey: authKeys.currentUser() });
				throw redirect({ to: Routes.LOGIN });
			}

			return user;
		} catch (error) {
			if (isRedirect(error)) throw error;
			throw redirect({ to: Routes.LOGIN });
		}
	}

	static async requireGuest(queryClient: QueryClient): Promise<void> {
		try {
			await queryClient.ensureQueryData({
				queryKey: authKeys.currentUser(),
				queryFn: getCurrentUser,
				staleTime: AUTH_STALE_TIME,
			});
			throw redirect({ to: Routes.HOME });
		} catch (error) {
			if (isRedirect(error)) throw error;
		}
	}
}
